import { type DataProvider } from "@refinedev/core";
import { axiosInstance } from "../../api/axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5177/api";

interface GetListParams {
    resource: string;
    pagination?: { current?: number; pageSize?: number };
    filters?: Array<{ field: string; operator: string; value: any }>;
    sorters?: Array<{ field: string; order: string }>;
}

interface GetOneParams {
    resource: string;
    id: string | number;
}

interface CreateParams {
    resource: string;
    variables: Record<string, any>;
}

interface UpdateParams {
    resource: string;
    id: string | number;
    variables: Record<string, any>;
}

interface DeleteOneParams {
    resource: string;
    id: string | number;
}

interface GetManyParams {
    resource: string;
    ids: Array<string | number>;
}

interface CreateManyParams {
    resource: string;
    variables: Record<string, any>;
}

interface DeleteManyParams {
    resource: string;
    ids: Array<string | number>;
}

interface UpdateManyParams {
    resource: string;
    ids: Array<string | number>;
    variables: Record<string, any>;
}

interface CustomParams {
    url: string;
    method: "get" | "post" | "put" | "patch" | "delete";
    filters?: Array<{ field: string; operator: string; value: any }>;
    sorters?: Array<{ field: string; order: string }>;
    payload?: Record<string, any>;
    query?: Record<string, any>;
}

interface GetSaleInvoiceParams {
    invoiceNumber: string;
}

axiosInstance.defaults.baseURL = API_URL;

axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.error('API Error:', error.response?.data || error.message);
        const customError = {
            ...error,
            message: error.response?.data?.message || "Something went wrong",
            statusCode: error.response?.status || 500,
        };
        return Promise.reject(customError);
    }
);

export const dataProvider = (): Partial<typeof DataProvider> => ({
    getList: async ({ resource, pagination, filters, sorters }: GetListParams) => {
        console.log('GetList request:', { resource, pagination, filters, sorters });
        const url = `${resource}`;
        const { data } = await axiosInstance.get(url, {
            params: {
                page: pagination?.current || 1,
                pageSize: pagination?.pageSize || 10,
                ...generateFilter(filters),
                ...generateSort(sorters?.[0]),
            },
        });
        console.log('GetList response first instance:', JSON.stringify(data[0]));
        return {
            data: data?.data || data || [],
            total: data?.data?.length || data?.length || 0,
        };
    },

    getOne: async ({ resource, id }: GetOneParams) => {
        console.log('GetOne request:', { resource, id });
        const { data } = await axiosInstance.get(`${resource}/${id}`);
        console.log('GetOne response:', data);
        return {
            data,
        };
    },

    create: async ({ resource, variables }: CreateParams) => {
        console.log('Create request:', { resource, variables });
        try {
            const { data } = await axiosInstance.post(`${resource}`, variables);
            console.log('Create response:', data);
            return {
                data,
            };
        } catch (error) {
            console.error('Create error:', error);
            throw error;
        }
    },

    update: async ({ resource, id, variables }: UpdateParams) => {
        console.log('Update request:', { resource, id, variables });
        const { data } = await axiosInstance.put(`${resource}/${id}`, variables);
        console.log('Update response:', data);
        return {
            data,
        };
    },

    deleteOne: async ({ resource, id }: DeleteOneParams) => {
        console.log('DeleteOne request:', { resource, id });
        const { data } = await axiosInstance.delete(`${resource}/${id}`);
        console.log('DeleteOne response:', data);
        return {
            data,
        };
    },

    getMany: async ({ resource, ids }: GetManyParams) => {
        console.log('GetMany request:', { resource, ids });
        const { data } = await axiosInstance.get(`${resource}`, {
            params: {
                ids: ids.join(","),
            },
        });
        console.log('GetMany response:', data);
        return {
            data,
        };
    },

    createMany: async ({ resource, variables }: CreateManyParams) => {
        console.log('CreateMany request:', { resource, variables });
        const { data } = await axiosInstance.post(`${resource}/bulk`, variables);
        console.log('CreateMany response:', data);
        return {
            data,
        };
    },

    deleteMany: async ({ resource, ids }: DeleteManyParams) => {
        console.log('DeleteMany request:', { resource, ids });
        const { data } = await axiosInstance.delete(`${resource}/bulk`, {
            data: { ids },
        });
        console.log('DeleteMany response:', data);
        return {
            data,
        };
    },

    updateMany: async ({ resource, ids, variables }: UpdateManyParams) => {
        console.log('UpdateMany request:', { resource, ids, variables });
        const { data } = await axiosInstance.put(`${resource}/bulk`, {
            ids,
            ...variables,
        });
        console.log('UpdateMany response:', data);
        return {
            data,
        };
    },

    custom: async ({ url, method, filters, sorters, payload, query }: CustomParams) => {
        console.log('Custom request:', { url, method, filters, sorters, payload, query });
        let axiosResponse;

        switch (method) {
            case "post":
            case "put":
            case "patch":
                axiosResponse = await axiosInstance[method](url, payload);
                break;
            case "delete":
                axiosResponse = await axiosInstance.delete(url, { data: payload });
                break;
            default:
                axiosResponse = await axiosInstance.get(url, {
                    params: {
                        ...query,
                        ...generateFilter(filters),
                        ...generateSort(sorters?.[0]),
                    },
                });
                break;
        }
        console.log('Custom response:', axiosResponse.data);
        return {
            data: axiosResponse.data,
        };
    },

    getApiUrl: () => API_URL,

    // Custom method to fetch sale invoice details by invoice number
    getSaleInvoice: async ({ invoiceNumber }: GetSaleInvoiceParams) => {
        console.log('GetSaleInvoice request:', { invoiceNumber });
        try {
            const { data } = await axiosInstance.get(`sales/invoice`, {
                params: {
                    invoiceNumber,
                },
            });
            console.log('GetSaleInvoice response:', data);
            return {
                data,
            };
        } catch (error) {
            console.error('GetSaleInvoice error:', error);
            throw error;
        }
    },
});

const generateSort = (sort?: { field: string; order: string }) => {
    if (sort && sort.field && sort.order) {
        return {
            sort: sort.field,
            order: sort.order,
        };
    }
    return {};
};

const generateFilter = (filters?: Array<{ field: string; operator: string; value: any }>) => {
    const queryFilters: { [key: string]: any } = {};

    if (filters) {
        filters.forEach((filter) => {
            if (filter.operator === "eq") {
                queryFilters[filter.field] = filter.value;
            }
        });
    }

    return queryFilters;
};

export default dataProvider();